import type { PlanPriority, PlanType, RefusalCategory } from '../types/agent.types';

// ============================================================================
// Plan type filter chips
// ============================================================================

export interface PlanTypeFilter {
  id: string;
  label: string;
  types: PlanType[];
}

export const PLAN_TYPE_FILTERS: PlanTypeFilter[] = [
  { id: 'all', label: 'Tous', types: [] },
  { id: 'paiement', label: 'Paiement', types: ['rappel_paiement_j1', 'rappel_paiement_j8'] },
  { id: 'expert', label: 'Expert', types: ['expert_en_retard'] },
  { id: 'documents', label: 'Documents', types: ['attente_doc_relance'] },
  { id: 'confirmation', label: 'Confirmation', types: ['attente_confirmation_relance'] },
  { id: 'emails', label: 'Emails', types: ['email_non_lu'] },
];

// ============================================================================
// Plan type card icons (SVG icon keys)
// ============================================================================

export const PLAN_TYPE_ICON_CONFIG: Record<string, { icon: 'payment' | 'expert' | 'document' | 'confirmation' | 'email'; className: string }> = {
  rappel_paiement_j1: { icon: 'payment', className: 'agent-card-icon--payment' },
  rappel_paiement_j8: { icon: 'payment', className: 'agent-card-icon--payment' },
  expert_en_retard: { icon: 'expert', className: 'agent-card-icon--expert' },
  attente_doc_relance: { icon: 'document', className: 'agent-card-icon--document' },
  attente_confirmation_relance: { icon: 'confirmation', className: 'agent-card-icon--confirmation' },
  email_non_lu: { icon: 'email', className: 'agent-card-icon--email' },
};

// ============================================================================
// Priority config
// ============================================================================

export interface PriorityConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  leftBorderColor: string;
  badgeClass: string;
}

export const PRIORITY_CONFIG: Record<PlanPriority, PriorityConfig> = {
  urgent: {
    label: 'Urgent',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    borderColor: '#FECACA',
    textColor: '#DC2626',
    leftBorderColor: '#DC2626',
    badgeClass: 'agent-badge--urgent',
  },
  high: {
    label: 'Haute',
    color: '#EA580C',
    bgColor: '#FFF7ED',
    borderColor: '#FED7AA',
    textColor: '#EA580C',
    leftBorderColor: '#EA580C',
    badgeClass: 'agent-badge--high',
  },
  medium: {
    label: 'Moyenne',
    color: '#D97706',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    textColor: '#D97706',
    leftBorderColor: '#D97706',
    badgeClass: 'agent-badge--medium',
  },
  low: {
    label: 'Basse',
    color: '#16A34A',
    bgColor: '#F0FDF4',
    borderColor: '#BBF7D0',
    textColor: '#16A34A',
    leftBorderColor: '#16A34A',
    badgeClass: 'agent-badge--low',
  },
};

// ============================================================================
// Step type icons (SVG icon keys)
// ============================================================================

export const STEP_TYPE_ICONS: Record<string, 'email' | 'sms' | 'tracker_com'> = {
  email: 'email',
  sms: 'sms',
  tracker_com: 'tracker_com',
};

// ============================================================================
// Refusal categories
// ============================================================================

export interface RefusalOption {
  id: RefusalCategory;
  label: string;
}

export const REFUSAL_CATEGORIES: RefusalOption[] = [
  { id: 'already_handled', label: 'Deja traite manuellement' },
  { id: 'not_needed', label: 'Pas necessaire pour ce dossier' },
  { id: 'bad_timing', label: 'Timing incorrect (trop tot / trop tard)' },
  { id: 'wrong_content', label: 'Mauvais destinataire ou contenu' },
  { id: 'other', label: 'Autre' },
];

// ============================================================================
// Status display config
// ============================================================================

export const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string; badgeClass: string }> = {
  pending: { label: 'En attente', color: '#F59E0B', bgColor: '#FFFBEB', borderColor: '#FDE68A', badgeClass: 'agent-badge--pending' },
  validated: { label: 'En cours', color: '#244785', bgColor: '#EEF2FF', borderColor: '#C7D2FE', badgeClass: 'agent-badge--validated' },
  executing: { label: 'En cours', color: '#244785', bgColor: '#EEF2FF', borderColor: '#C7D2FE', badgeClass: 'agent-badge--executing' },
  completed: { label: 'Execute', color: '#10B981', bgColor: '#F0FDF4', borderColor: '#BBF7D0', badgeClass: 'agent-badge--completed' },
  cancelled: { label: 'Annule', color: '#94A3B8', bgColor: '#F8FAFC', borderColor: '#E2E8F0', badgeClass: 'agent-badge--cancelled' },
  failed: { label: 'Erreur', color: '#DC2626', bgColor: '#FEF2F2', borderColor: '#FECACA', badgeClass: 'agent-badge--failed' },
};

// ============================================================================
// Review status display
// ============================================================================

export const REVIEW_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string; badgeClass: string }> = {
  pending: { label: 'En attente', color: '#F59E0B', bgColor: 'transparent', borderColor: 'transparent', badgeClass: '' },
  approved: { label: 'Approuve', color: '#10B981', bgColor: '#F0FDF4', borderColor: '#BBF7D0', badgeClass: 'agent-step-review-badge--approved' },
  modified: { label: 'Modifie', color: '#3B82F6', bgColor: '#EFF6FF', borderColor: '#BFDBFE', badgeClass: 'agent-step-review-badge--modified' },
  refused: { label: 'Refuse', color: '#EF4444', bgColor: '#FEF2F2', borderColor: '#FECACA', badgeClass: 'agent-step-review-badge--refused' },
};
